<script setup lang="ts">
import { ref } from "vue";
import { VueDatePicker } from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { InvitedUserInterface } from "../../../interface/UserInterface";
import { appContainer } from "../../../container";
import { LoadingService } from "../../../services/loadingService";
import { ToastService } from "../../../services/toastService";
import { UserService } from "../../../services/userService";
import { AxiosError } from "axios";
import { ErrorResponse } from "../../../interface/ErrorInterface";

const props = defineProps<{
  invitation: InvitedUserInterface;
}>();

const emit = defineEmits(["reopened", "close"]);

const userService = appContainer.resolve(UserService);
const loadingService = appContainer.resolve(LoadingService);
const toastService = appContainer.resolve(ToastService);

const feedbackCompletionDate = ref<string>("");
const formError = ref<string>("");

function validateForm(): boolean {
  formError.value = "";

  if (!feedbackCompletionDate.value) {
    formError.value = "Please select the Assessment End Date.";
    return false;
  }

  const selectedDate = new Date(feedbackCompletionDate.value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (selectedDate < today) {
    formError.value = "Assessment End Date must be today or a future date.";
    return false;
  }

  return true;
}

async function handleReopenAssessment() {
  if (!validateForm()) {
    return;
  }

  const loader = await loadingService.show();

  userService
    .reopenAssessment(props.invitation.id, feedbackCompletionDate.value)
    .then(() => {
      toastService.success("Assessment Reopened Successfully");
      emit("reopened");
    })
    .catch((err) => {
      const error = err as AxiosError;
      const errorResponse = error.response?.data as ErrorResponse;
      if (errorResponse?.errors) {
        const flattenedErrors = Object.values(errorResponse.errors).flat();
        toastService.error(flattenedErrors.join("<br>"));
      } else {
        toastService.error("Unable to Reopen Assessment");
      }
    })
    .finally(() => loader.hide());
}
</script>

<template>
  <div class="modal-backdrop">
    <div class="modal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <p class="modal-title fw-bold">Reopen Assessment</p>
            <button
              type="button"
              class="btn-close"
              @click="emit('close')"
            ></button>
          </div>
          <div class="modal-body">
            <p class="text-center mt-2 px-2">
              Reopen assessment for
              <strong class="text-capitalize">
                {{ invitation.invited_user.first_name }}
                {{ invitation.invited_user.last_name }}
              </strong>
              <span class="break-word ms-1">
                ({{ invitation.invited_user.email }})
              </span>
            </p>

            <div class="mb-3 px-3">
              <label for="reopenDate" class="form-label fw-bold">
                New Assessment End Date
              </label>
              <VueDatePicker
                id="reopenDate"
                v-model="feedbackCompletionDate"
                :min-date="new Date()"
                :week-start="0"
                :enable-time-picker="false"
                placeholder="Select Assessment End Date"
                format="MM-dd-yyyy"
                model-type="yyyy-MM-dd"
                auto-apply
                text-input
                :teleport="true"
                input-class-name="form-control"
                :time-config="{ enableTimePicker: false }"
              />
              <div v-if="formError" class="text-danger mt-2 error">
                {{ formError }}
              </div>
            </div>

            <div
              class="d-flex align-items-center justify-content-evenly p-4 gap-3 flex-wrap"
            >
              <button
                type="button"
                class="btn btn-danger btn-sm text-white btn-action"
                @click="emit('close')"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-secondary btn-sm text-white btn-action"
                @click="handleReopenAssessment"
              >
                Reopen Assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.btn-action {
  width: calc(50% - 1.5rem);
}

.error {
  font-size: 0.875rem;
}

:deep(.dp__input) {
  border: 1px solid #dee2e6 !important;
  border-radius: 0.375rem !important;
  font-size: 1rem !important;
  line-height: 1.5 !important;
  color: #212529 !important;
  background-color: #fff !important;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out !important;
}

:deep(.dp__input:hover) {
  border-color: #dee2e6 !important;
}

:deep(.dp__input:focus) {
  border-color: #86b7fe !important;
  outline: 0 !important;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
}

:deep(.dp__input_wrap) {
  width: 100% !important;
}

@media (max-width: 767px) {
  .btn-action {
    width: 100%;
  }
}
</style>
